import { ArrowDown, ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react"
import { Link } from "react-router-dom"
import { workDetailPath, workPath } from "~/common/paths"
import { caseStudyPresentations, type CaseStudyStory } from "~/content/case-study-presentations"
import { caseStudies, type CaseStudyId } from "~/content/cases"
import type { TestimonialId } from "~/content/testimonials"
import type { CaseStudy } from "~/content/types"
import { workIndexCaseStudyIds } from "~/content/work-index"
import { SourceContactEndcap } from "~/marketing/public-page"
import { TestimonialQuote } from "~/marketing/testimonial-quote"
import { WorkCover } from "~/marketing/work-cover"

const StoryVisual = ({ story, name }: { story: CaseStudyStory; name: string }) => {
  if (story.cover) return <WorkCover id={story.cover} />
  if (story.steps) {
    return (
      <div className="case-workflow">
        <p>How the work fits together</p>
        <ol>
          {story.steps.map((step, index) => (
            <li key={step}>
              {index > 0 && <ArrowDown aria-hidden="true" />}
              <span>
                {index + 1}.&nbsp; {step}
              </span>
            </li>
          ))}
        </ol>
      </div>
    )
  }
  return (
    <img
      className="case-project-image"
      src={story.image}
      alt={`${name}: ${story.heading}`}
      width={620}
      height={460}
      loading="lazy"
      decoding="async"
    />
  )
}

const caseTestimonials: Partial<Record<CaseStudyId, TestimonialId>> = {
  skillschool: "bre-pettis",
}

export const CaseStudyTemplate = ({ caseStudy }: { caseStudy: CaseStudy }) => {
  const slug = caseStudy.slug as CaseStudyId
  const testimonial = caseTestimonials[slug]
  const presentation = caseStudyPresentations[slug]
  const nextId =
    workIndexCaseStudyIds[(workIndexCaseStudyIds.indexOf(slug) + 1) % workIndexCaseStudyIds.length]
  const next = caseStudies[nextId]
  const productLinks = [
    ...(slug === "the-information"
      ? [{ label: "Explore The Information", href: "https://www.theinformation.com" }]
      : []),
    ...(slug === "dapper-labs"
      ? [
          {
            label: "Explore Flow developer tools",
            href: "https://developers.flow.com/tools/flow-wallet",
          },
        ]
      : []),
    ...((slug === "the-information" || slug === "hadley") && caseStudy.appLinks
      ? [
          { label: "App Store", href: caseStudy.appLinks.ios },
          { label: "Google Play", href: caseStudy.appLinks.android },
        ]
      : []),
  ]

  return (
    <div className="case-detail-page" key={slug}>
      <section className="container case-project-intro">
        <Link to={workPath({})} className="case-back-link">
          <ArrowLeft aria-hidden="true" /> Case Studies
        </Link>
        <div className="case-project-hero">
          <div className="case-project-promise">
            <p className="case-project-eyebrow">{caseStudy.name}</p>
            <h1>{presentation.headline}</h1>
            <p className="case-project-summary">{presentation.summary}</p>
          </div>
          <WorkCover id={slug} />
        </div>
        <dl className="case-project-facts">
          {presentation.facts.map((fact) => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section className="case-project-band">
        <div className="container">
          <p className="case-project-eyebrow">The brief</p>
          <div className="case-project-brief">
            <h2>{presentation.brief}</h2>
            <p>{presentation.briefBody}</p>
          </div>
        </div>
      </section>
      <section className="container case-project-stories" aria-label="The project">
        {presentation.rows.map((story) => (
          <article className="case-project-story" key={story.heading}>
            <StoryVisual story={story} name={caseStudy.name} />
            <div className="case-project-copy">
              <h2>{story.heading}</h2>
              <p>{story.body}</p>
            </div>
          </article>
        ))}
      </section>
      <section className="case-project-band case-project-outcome">
        <div className="container">
          <p className="case-project-eyebrow">{presentation.outcome.label}</p>
          {presentation.outcome.quote ? (
            <figure>
              <blockquote>{presentation.outcome.heading}</blockquote>
              <figcaption>{presentation.outcome.body}</figcaption>
            </figure>
          ) : (
            <>
              <h2>{presentation.outcome.heading}</h2>
              <p>{presentation.outcome.body}</p>
            </>
          )}
        </div>
      </section>
      {testimonial && <TestimonialQuote id={testimonial} />}
      {productLinks.length > 0 && (
        <nav className="container case-project-links" aria-label="Explore the product">
          {productLinks.map((link) => (
            <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer">
              {link.label} <ArrowUpRight aria-hidden="true" />
            </a>
          ))}
        </nav>
      )}
      <nav className="container case-project-next" aria-label="Next case study">
        <p className="case-project-eyebrow">Next project</p>
        <Link to={workDetailPath({ slug: nextId })}>
          {next.name} <ArrowRight aria-hidden="true" />
        </Link>
      </nav>
      <SourceContactEndcap workPage showNewsletter={false} />
    </div>
  )
}
